import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatCurrency, toMoneyString } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import {
  PAYMENT_METHODS,
  type CashierShift,
  type Check,
  type Payment,
  type PaymentMethod,
  apiError,
  balanceOf,
  fromMinor,
  idempotencyKey,
  moneyStep,
  toMinor,
} from './lib';

/**
 * D-01 — a timeout is not a failure, it is an *unknown*.
 *
 * `axios` gives up after 20s (`packages/api-client/src/http.ts`), but the POST
 * may well have been committed server-side by then. The only safe response is
 * to press the same button again with the *same* idempotency key, so the second
 * attempt is a replay the backend collapses rather than a second charge — which
 * is exactly what a per-dialog key buys. Everything else (a 4xx, a validation
 * failure) carries a response body and is genuinely terminal.
 */
function isUncertain(error: unknown): boolean {
  const e = error as { code?: string; response?: unknown; isAxiosError?: boolean } | null;
  // A response — any status — means the server answered, so the outcome is known.
  if (!e || e.response) return false;
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || e.code === 'ERR_NETWORK') return true;
  // Any other transport failure that never saw a response is equally unknown.
  return e.isAxiosError === true;
}

/** The banner shown once a submit has timed out, in place of a bare error. */
function RetrySafely({ text }: { text: string }) {
  return (
    <p
      role="status"
      className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
    >
      {text}
    </p>
  );
}

interface TakeProps {
  check: Check;
  shift: CashierShift | null;
  onClose: () => void;
  onDone: () => void;
  /**
   * Refuse a CASH payment while no drawer is open.
   *
   * `PaymentsService.resolveShiftId` books cash to the caller's open shift on
   * the branch and, finding none, books it to `null` — the payment succeeds and
   * the money lands nowhere `expectedCash` can count it, so the drawer balances
   * at close on a figure that is short by exactly that sale. There is no error
   * anywhere to explain it afterwards.
   *
   * On for the register (`Billing/Register.tsx`), which IS a drawer: a till with
   * no shift open is not in service, and the shift strip above it is one press
   * from opening one. Off by default, because the two screens that mounted this
   * before — the day's check list and the floor map's Bill pane — are also used
   * to record a card settlement or to reconcile a bill after the fact, and
   * turning the refusal on for them is a behaviour change this does not make.
   * Only CASH is ever refused; every other method is drawer-independent.
   */
  cashRequiresShift?: boolean;
}

/** §17 — payments settle a *check*, never an order. Partial is the default case. */
export function TakePaymentDialog({
  check,
  shift,
  onClose,
  onDone,
  cashRequiresShift = false,
}: TakeProps) {
  const { t, i18n } = useTranslation();
  // A payment is denominated in the check it settles — never in a screen-level
  // default — so the entry step and the string sent both follow `check.currency`.
  const currency = check.currency;
  const step = moneyStep(currency);
  const outstanding = balanceOf(check);
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [amount, setAmount] = useState(outstanding);
  const [tipAmount, setTipAmount] = useState(() => toMoneyString(0, currency));
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  // D-01 — minted ONCE per dialog instance, not per submit. `disabled={saving}`
  // only covers a double-click while the request is in flight; after a 20s
  // axios timeout the button re-enables and the cashier presses again. With a
  // stable key that second press is a replay the backend already collapses;
  // with a fresh UUID it was an unrelated payment on the same check.
  const [key] = useState(() => idempotencyKey(`pay:${check.id}`));
  const [uncertain, setUncertain] = useState(false);

  const remainingAfter = fromMinor(
    toMinor(outstanding, currency) - toMinor(amount, currency),
    currency,
  );
  const isPartial = toMinor(amount, currency) < toMinor(outstanding, currency);
  /** Cash, with no drawer to book it to, on a screen that will not have that. */
  const cashRefused = cashRequiresShift && method === 'CASH' && !shift;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/payments', {
        checkId: check.id,
        method,
        amount: toMoneyString(amount, currency),
        tipAmount:
          toMinor(tipAmount, currency) > 0 ? toMoneyString(tipAmount, currency) : undefined,
        reference: reference || undefined,
        shiftId: method === 'CASH' ? (shift?.id ?? undefined) : undefined,
        idempotencyKey: key,
      });
      toast.success(t('billing.payment.taken'));
      onDone();
      onClose();
    } catch (e) {
      if (isUncertain(e)) {
        setUncertain(true);
        toast.warning(
          t('billing.payment.uncertain'),
        );
      } else {
        toast.error(apiError(e, t('billing.payment.failed')));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('billing.payment.title', { number: check.number })}
      description={t('billing.payment.outstanding', {
        amount: formatCurrency(outstanding, currency, i18n.language),
      })}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={saving || cashRefused || toMinor(amount, currency) <= 0}
            title={cashRefused ? t('billing.payment.cashNeedsShift') : undefined}
          >
            {uncertain ? t('billing.payment.retry') : t('billing.payment.take')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {uncertain && (
          <RetrySafely
            text={t('billing.payment.uncertainHint')}
          />
        )}
        <Field label={t('billing.payment.method')}>
          <Select
            value={method}
            disabled={uncertain}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
          >
            {PAYMENT_METHODS.map((value) => (
              <option key={value} value={value}>
                {t(`billing.method.${value}`)}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('billing.payment.amount')}
            hint={
              isPartial
                ? t('billing.payment.partialHint', {
                    amount: formatCurrency(remainingAfter, currency, i18n.language),
                  })
                : t('billing.payment.fullHint')
            }
          >
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step={step}
              value={amount}
              disabled={uncertain}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <Field label={t('common.tip')} hint={t('billing.payment.tipHint')}>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step={step}
              value={tipAmount}
              disabled={uncertain}
              onChange={(e) => setTipAmount(e.target.value)}
            />
          </Field>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={uncertain}
            onClick={() => setAmount(outstanding)}
          >
            {t('billing.payment.fillFull')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={uncertain}
            onClick={() =>
              setAmount(fromMinor(Math.floor(toMinor(outstanding, currency) / 2), currency))
            }
          >
            {t('billing.payment.fillHalf')}
          </Button>
        </div>

        <Field label={t('billing.payment.reference')} hint={t('billing.payment.referenceHint')}>
          <Input
            value={reference}
            disabled={uncertain}
            onChange={(e) => setReference(e.target.value)}
          />
        </Field>

        {cashRefused && (
          <p
            role="status"
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
          >
            {t('billing.payment.cashNeedsShift')}
          </p>
        )}

        {method === 'CASH' && !cashRefused && (
          <p className="text-xs text-ink-muted">
            {shift
              ? t('billing.payment.booksToShift', { id: shift.id.slice(-6) })
              : t('billing.payment.noShift')}
          </p>
        )}
      </div>
    </Dialog>
  );
}

interface RefundProps {
  payment: Payment;
  onClose: () => void;
  onDone: () => void;
}

/** P9-06 — a refund needs `payments.refund` *and* a stated reason. */
export function RefundDialog({ payment, onClose, onDone }: RefundProps) {
  const { t, i18n } = useTranslation();
  const currency = payment.currency;
  const step = moneyStep(currency);
  const refundable = fromMinor(
    toMinor(payment.amount, currency) - toMinor(payment.refundedAmount, currency),
    currency,
  );
  const [amount, setAmount] = useState(refundable);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // D-01 — one key per dialog. A refund retried under a fresh key is money out
  // of the drawer twice.
  const [key] = useState(() => idempotencyKey(`refund:${payment.id}`));
  const [uncertain, setUncertain] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/payments/${payment.id}/refund`, {
        amount: toMoneyString(amount, currency),
        reason,
        idempotencyKey: key,
      });
      toast.success(t('billing.refund.done'));
      onDone();
      onClose();
    } catch (e) {
      if (isUncertain(e)) {
        setUncertain(true);
        toast.warning(
          t('billing.refund.uncertain'),
        );
      } else {
        toast.error(apiError(e, t('billing.refund.failed')));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('billing.refund.title')}
      description={t('billing.refund.subtitle', {
        amount: formatCurrency(refundable, currency, i18n.language),
      })}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={submit} disabled={saving || reason.trim().length < 3}>
            {uncertain ? t('billing.payment.retry') : t('billing.refund.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {uncertain && (
          <RetrySafely
            text={t('billing.refund.uncertainHint')}
          />
        )}
        <Field label={t('billing.refund.amount')} hint={t('billing.refund.amountHint')}>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step={step}
            value={amount}
            disabled={uncertain}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label={t('billing.refund.reason')} required>
          <Textarea
            rows={3}
            value={reason}
            disabled={uncertain}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/** §17 — a tip added after the fact; it never reduces the check balance. */
export function TipDialog({ payment, onClose, onDone }: RefundProps) {
  const { t } = useTranslation();
  // A tip lands on the payment, so it is in that payment's currency.
  const currency = payment.currency;
  const step = moneyStep(currency);
  const [amount, setAmount] = useState(() => toMoneyString(0, currency));
  const [saving, setSaving] = useState(false);
  // D-01 — one key per dialog, so a retried tip is a replay and not a second one.
  const [key] = useState(() => idempotencyKey(`tip:${payment.id}`));
  const [uncertain, setUncertain] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/payments/${payment.id}/tip`, {
        amount: toMoneyString(amount, currency),
        idempotencyKey: key,
      });
      toast.success(t('billing.tip.done'));
      onDone();
      onClose();
    } catch (e) {
      if (isUncertain(e)) {
        setUncertain(true);
        toast.warning(
          t('billing.tip.uncertain'),
        );
      } else {
        toast.error(apiError(e, t('billing.tip.failed')));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('billing.tip.title')}
      description={t('billing.tip.subtitle')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving || toMinor(amount, currency) <= 0}>
            {uncertain ? t('billing.payment.retry') : t('billing.tip.confirm')}
          </Button>
        </>
      }
    >
      {uncertain && (
        <RetrySafely
          text={t('billing.tip.uncertainHint')}
        />
      )}
      <Field label={t('common.tip')}>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step={step}
          value={amount}
          disabled={uncertain}
          onChange={(e) => setAmount(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}
